import { useEffect, useRef } from "react";

type TurnstileOptions = {
    sitekey: string;
    callback: (token: string) => void;
    "expired-callback": () => void;
    "error-callback": () => void;
};

declare global {
    interface Window {
        turnstile?: {
            render: (
                container: HTMLElement,
                options: TurnstileOptions
            ) => string;
            remove: (widgetId: string) => void;
        };
    }
}

type Props = {
    onToken: (token: string | null) => void;
};

const SCRIPT_SRC =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Without REACT_APP_TURNSTILE_SITE_KEY, local falls back to Cloudflare's test key, which always passes
// and only works against an api configured with the matching test secret. A deployed build without it
// shows no widget.
const LOCAL_TEST_SITE_KEY = "1x00000000000000000000AA";

export const TURNSTILE_SITE_KEY =
    process.env.REACT_APP_TURNSTILE_SITE_KEY ||
    (process.env.REACT_APP_ENV === "local" ? LOCAL_TEST_SITE_KEY : "");

let scriptLoading: Promise<void> | null = null;

const loadScript = (): Promise<void> => {
    if (!scriptLoading) {
        scriptLoading = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = SCRIPT_SRC;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                scriptLoading = null;
                reject(new Error("Could not load the captcha"));
            };
            document.head.appendChild(script);
        });
    }
    return scriptLoading;
};

/**
 * Cloudflare Turnstile widget for public signup forms. Reports a token once the visitor passes,
 * and null when it expires or fails. A token is single-use: remount the widget (change its key)
 * after a submit so the next attempt gets a fresh one. Renders nothing when this environment has
 * no site key.
 */
const Turnstile = ({ onToken }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    useEffect(() => {
        if (!TURNSTILE_SITE_KEY) {
            return;
        }

        let widgetId: string | null = null;
        let cancelled = false;

        loadScript()
            .then(() => {
                if (cancelled || !containerRef.current || !window.turnstile) {
                    return;
                }
                widgetId = window.turnstile.render(containerRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    callback: (token) => onTokenRef.current(token),
                    "expired-callback": () => onTokenRef.current(null),
                    "error-callback": () => onTokenRef.current(null),
                });
            })
            .catch(() => onTokenRef.current(null));

        return () => {
            cancelled = true;
            if (widgetId && window.turnstile) {
                window.turnstile.remove(widgetId);
            }
        };
    }, []);

    if (!TURNSTILE_SITE_KEY) {
        return null;
    }

    return <div ref={containerRef} className="flex justify-center" />;
};

export default Turnstile;
